const SITE_URL = "https://askdollarbill.com";

export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Dollar Bill",
    url: SITE_URL,
    logo: `${SITE_URL}/favicon.svg`,
    description:
      "Financial clarity for small food businesses. Weekly P&Ls, automated invoice parsing, menu profitability, and an AI CFO you can ask anything.",
    email: "hello@askdollarbill.com",
    foundingDate: "2025",
    areaServed: "US",
    sameAs: [
      "https://www.linkedin.com/company/askdollarbill",
      "https://www.instagram.com/askdollarbill",
    ],
  };
}

export function webSiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Dollar Bill",
    url: SITE_URL,
    description:
      "Financial clarity for small food businesses. Stop guessing. Start knowing.",
    potentialAction: {
      "@type": "SearchAction",
      target: `${SITE_URL}/blog?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
}

export function softwareApplicationSchema(overrides?: {
  name?: string;
  description?: string;
  audience?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: overrides?.name || "Dollar Bill",
    applicationCategory: "FinanceApplication",
    operatingSystem: "Web",
    description:
      overrides?.description ||
      "Financial clarity for small food businesses. Weekly P&Ls, menu profitability, and an AI CFO.",
    ...(overrides?.audience && {
      audience: {
        "@type": "Audience",
        audienceType: overrides.audience,
      },
    }),
  };
}

export function productSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "Dollar Bill",
    description:
      "Financial clarity for small food businesses. Weekly P&Ls, menu profitability analysis, and a CFO you can ask anything.",
    brand: {
      "@type": "Brand",
      name: "Dollar Bill",
    },
  };
}

export function howToSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "How to Get Financial Clarity for Your Food Business",
    description:
      "Three steps to get weekly P&Ls, menu profitability, and clear financial answers for your restaurant or food business.",
    totalTime: "PT15M",
    step: [
      {
        "@type": "HowToStep",
        position: 1,
        name: "Connect your accounts",
        text: "Link your POS system (Square, Toast, Clover) and bank account. Dollar Bill pulls real transaction data automatically.",
      },
      {
        "@type": "HowToStep",
        position: 2,
        name: "Train the system",
        text: "Upload recent invoices and categorize your expenses. Tell us about your menu. Dollar Bill learns how your business works.",
      },
      {
        "@type": "HowToStep",
        position: 3,
        name: "Talk to Bill",
        text: "Ask any question about your business finances. Bill gives you clear answers with data to back them up, plus actions you can take.",
      },
    ],
  };
}

export function faqSchema(
  faqs: Array<{ question: string; answer: string }>
) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
}

export function articleSchema(post: {
  title: string;
  description: string;
  slug: string;
  publishedAt: string;
  updatedAt?: string;
  author: string;
  ogImage?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.description,
    url: `${SITE_URL}/blog/${post.slug}`,
    datePublished: post.publishedAt,
    dateModified: post.updatedAt || post.publishedAt,
    author: {
      "@type": "Person",
      name: post.author,
    },
    publisher: {
      "@type": "Organization",
      name: "Dollar Bill",
      logo: {
        "@type": "ImageObject",
        url: `${SITE_URL}/favicon.svg`,
      },
    },
    image: post.ogImage || `${SITE_URL}/og-image.png`,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${SITE_URL}/blog/${post.slug}`,
    },
  };
}

export function breadcrumbSchema(
  items: Array<{ name: string; url: string }>
) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: `${SITE_URL}${item.url}`,
    })),
  };
}

export function localBusinessSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Dollar Bill",
    description: "Financial clarity for small food businesses.",
    url: SITE_URL,
    provider: {
      "@type": "Organization",
      name: "Dollar Bill",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Houston",
        addressRegion: "TX",
        addressCountry: "US",
      },
    },
  };
}
